import api from '../utils/api';

/**
 * @typedef {Object} Course
 * @property {string} _id
 * @property {string} title
 * @property {string} slug
 * @property {string} description
 * @property {string|{_id: string, user?: {firstName: string, lastName: string, profilePicture?: string}}} instructor
 * @property {string} level - 'beginner', 'intermediate', 'advanced'
 * @property {string} language - 'Arabic', 'English', 'Both'
 * @property {string|{_id: string, name: string}} trade
 * @property {number} price
 * @property {number} [discountPrice]
 * @property {boolean} isFree
 * @property {number} maxFreeLessons
 * @property {number|null} [accessDurationDays]
 * @property {string} [thumbnail]
 * @property {string} [thumbnailVideoUrl]
 * @property {string} [previewVideo]
 * @property {number} totalDuration
 * @property {number} totalLessons
 * @property {number} totalModules
 * @property {number} enrollmentCount
 * @property {number} averageRating
 * @property {number} totalReviews
 * @property {string[]} learningOutcomes
 * @property {string[]} requirements
 * @property {string[]} targetAudience
 * @property {boolean} isPublished
 * @property {string} [publishedAt]
 * @property {boolean} certificateAvailable
 * @property {string[]} tags
 * @property {boolean} [isTrending]
 * @property {number} [orderIndex]
 * @property {number} [duration]
 * @property {string} status - 'draft', 'published', 'archived'
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef {Object} CreateCourseData
 * @property {string} title
 * @property {string} slug
 * @property {string} description
 * @property {string} trade
 * @property {string} [instructor]
 * @property {string} level
 * @property {string} language
 * @property {number} [price]
 * @property {number} [discountPrice]
 * @property {boolean} [isFree]
 * @property {number} [maxFreeLessons]
 * @property {string} [thumbnail]
 * @property {string} [thumbnailVideoUrl]
 * @property {string} [previewVideo]
 * @property {string[]} [learningOutcomes]
 * @property {string[]} [requirements]
 * @property {string[]} [targetAudience]
 * @property {string[]} [tags]
 * @property {boolean} [certificateAvailable]
 */

/**
 * @typedef {Object} UpdateAccessData
 * @property {boolean} [isFree]
 * @property {boolean} [isPrivate]
 * @property {string} [accessType]
 */

/**
 * @typedef {Object} UpdatePricingData
 * @property {number} price
 * @property {number} [salePrice]
 * @property {string} [currency]
 */

export const courseService = {
  // ==================
  // PUBLIC ROUTES
  // ==================

  /**
   * Get all courses with optional filters
   * @param {Object} [params] - specialized query params
   * @returns {Promise<{success: boolean, count: number, data: {courses: Course[], pagination: Object}}>}
   */
  getAllCourses: async (params = {}) => {
    const response = await api.get('/api/courses', { params });
    return response;
  },

  /**
   * Get featured courses
   * @returns {Promise<{status: string, courses: Course[]}>}
   */
  getFeaturedCourses: async () => {
    const response = await api.get('/api/courses/featured');
    return response;
  },

  /**
   * Get popular courses
   * @returns {Promise<{status: string, courses: Course[]}>}
   */
  getPopularCourses: async () => {
    const response = await api.get('/api/courses/popular');
    return response;
  },

  /**
   * Get courses by trade ID
   * @param {string} tradeId 
   * @returns {Promise<{status: string, courses: Course[]}>}
   */
  getCoursesByTrade: async (tradeId) => {
    const response = await api.get(`/api/courses/trade/${tradeId}`);
    return response;
  },

  /**
   * Get courses by instructor ID
   * @param {string} instructorId 
   * @returns {Promise<{status: string, courses: Course[]}>}
   */
  getCoursesByInstructor: async (instructorId) => {
    const response = await api.get(`/api/courses/instructor/${instructorId}`);
    return response;
  },

  /**
   * Get course by slug
   * @param {string} slug 
   * @returns {Promise<{status: string, course: Course}>}
   */
  getCourseBySlug: async (slug) => {
    const response = await api.get(`/api/courses/slug/${slug}`);
    return response;
  },

  /**
   * Get course access info (Public)
   * @param {string} courseId 
   * @returns {Promise<{status: string, access: Object}>}
   */
  getCourseAccessInfo: async (courseId) => {
    const response = await api.get(`/api/courses/${courseId}/access-info`);
    return response;
  },

  /**
   * Get course by ID
   * @param {string} id 
   * @returns {Promise<{status: string, course: Course}>}
   */
  getCourseById: async (id) => {
    const response = await api.get(`/api/courses/${id}`);
    return response;
  },

  // ==================
  // PROTECTED ROUTES (Student)
  // ==================

  /**
   * Get my enrolled courses
   * @returns {Promise<{status: string, courses: Course[]}>}
   */
  getMyEnrolledCourses: async () => {
    const response = await api.get('/api/courses/enrolled/my-courses');
    return response;
  },

  /**
   * Get single enrolled course details
   * @param {string} courseId 
   * @returns {Promise<{status: string, course: Course, progress: number}>}
   */
  getMyEnrolledCourseById: async (courseId) => {
    const response = await api.get(`/api/courses/enrolled/${courseId}/details`);
    return response;
  },

  /**
   * Check enrollment status
   * @param {string} courseId 
   * @returns {Promise<{status: string, isEnrolled: boolean}>}
   */
  checkEnrollmentStatus: async (courseId) => {
    const response = await api.get(`/api/courses/enrolled/${courseId}/status`);
    return response;
  },

  // ==================
  // INSTRUCTOR/ADMIN ROUTES
  // ==================

  /**
   * Get my courses (Instructor/Admin)
   * @returns {Promise<{status: string, courses: Course[]}>}
   */
  getMyCourses: async () => {
    const response = await api.get('/api/courses/my/courses');
    return response;
  },

  /**
   * Get course statistics
   * @param {string} id 
   * @returns {Promise<{status: string, statistics: CourseStats}>}
   */
  getCourseStatistics: async (id) => {
    const response = await api.get(`/api/courses/${id}/statistics`);
    return response;
  },

  /**
   * Create course
   * @param {CreateCourseData} courseData 
   * @returns {Promise<{status: string, course: Course}>}
   */
  createCourse: async (courseData) => {
    const response = await api.post('/api/courses', courseData);
    return response;
  },

  /**
   * Update course
   * @param {string} id 
   * @param {Object} updates 
   * @returns {Promise<{status: string, course: Course}>}
   */
  updateCourse: async (id, updates) => {
    const response = await api.put(`/api/courses/${id}`, updates);
    return response;
  },

  /**
   * Toggle publish status
   * @param {string} id 
   * @returns {Promise<{status: string, message: string, isPublished: boolean}>}
   */
  togglePublishCourse: async (id) => {
    const response = await api.patch(`/api/courses/${id}/toggle-publish`);
    return response;
  },

  /**
   * Update course access settings
   * @param {string} courseId 
   * @param {UpdateAccessData} settings 
   * @returns {Promise<{status: string, message: string}>}
   */
  updateCourseAccessSettings: async (courseId, settings) => {
    const response = await api.put(`/api/courses/${courseId}/access-settings`, settings);
    return response;
  },

  /**
   * Update course pricing
   * @param {string} courseId 
   * @param {UpdatePricingData} pricing 
   * @returns {Promise<{status: string, message: string}>}
   */
  updateCoursePricing: async (courseId, pricing) => {
    const response = await api.put(`/api/courses/${courseId}/pricing`, pricing);
    return response;
  },

  // ==================
  // ADMIN ONLY ROUTES
  // ==================

  /**
   * Delete course
   * @param {string} id 
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteCourse: async (id) => {
    const response = await api.delete(`/api/courses/${id}`);
    return response;
  },

  /**
   * Reorder courses
   * @param {Array<{id: string, orderIndex: number}>} courses 
   * @returns {Promise<{status: string, message: string}>}
   */
  reorderCourses: async (courses) => {
    const response = await api.patch('/api/courses/reorder', { courses });
    return response;
  },

  /**
   * Resync all course stats (Admin only)
   * Recalculates totalLessons, totalDuration, duration for all courses
   * @returns {Promise<{status: string, message: string, data: Array}>}
   */
  resyncAllCourseStats: async () => {
    const response = await api.post('/api/courses/admin/resync-stats');
    return response;
  },

  // ==================
  // ADMIN EXPORT ROUTES
  // ==================

  /**
   * Get courses count (Admin - for export preview)
   * @param {Object} [params] - Query parameters (isPublished, search, fromDate, toDate, etc.)
   * @returns {Promise<{status: string, total: number}>}
   */
  getCoursesCount: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `/api/courses/admin/count${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response;
  },

  /**
   * Export courses without pagination (Admin)
   * @param {Object} [params] - Query parameters (isPublished, search, fromDate, toDate, etc.)
   * @returns {Promise<{status: string, count: number, courses: Course[]}>}
   */
  exportCourses: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `/api/courses/admin/export${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response;
  },
};
